import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Grid, Typography } from 'gu-ui-kit'
import format from 'utils/dateFns/format'
import { parse } from 'date-fns'
import { StatusBadge } from 'modules/Tickets/List/subcomponents/StatusBadge'
import styles from 'modules/Tickets/Chat/ticket.module.scss'
import Message from './subcomponents/Message'
import NewTicketMessage, { validationSchema } from 'modules/Forms/Tickets/NewTicketMessage'
import { useTranslation } from 'react-i18next'
import validator from 'utils/asyncValidator'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import {
  CLEAR,
  CLOSE,
  FETCH_TICKET,
  PANEL_NAVIGATE,
  REOPEN,
  SET_DATA,
  SET_LOADING,
} from 'modules/Tickets/types'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import clsx from 'clsx'
import { initialize } from 'redux-form'
import Breadcrumbs from 'components/Breadcrumbs'
import PageHeader from 'components/PageHeader'
import { Placeholder } from 'modules/Tickets/Chat/subcomponents/Placeholder'

const ToggleButton = ({ smallHeader, isNew, id, status }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const breakpoint = useBreakpoint()

  return (
    <>
      {!isNew && status !== 'closed' && (
        <Button
          fullWidth={!breakpoint.sm && !smallHeader}
          size={'s'}
          variant={'secondary'}
          grey
          noWrap
          onClick={() =>
            dispatch({
              type: CLOSE,
              payload: { id },
            })
          }
        >
          {t('tickets::close_ticket')}
        </Button>
      )}

      {status === 'closed' && (
        <Button
          fullWidth={!breakpoint.sm && !smallHeader}
          size={'s'}
          variant={'secondary'}
          grey
          noWrap
          onClick={() =>
            dispatch({
              type: REOPEN,
              payload: { id },
            })
          }
        >
          {t('tickets::reopen_ticket')}
        </Button>
      )}
    </>
  )
}

ToggleButton.propTypes = {
  smallHeader: PropTypes.bool,
  isNew: PropTypes.bool,
  id: PropTypes.string,
  status: PropTypes.string,
}

const Ticket = ({ params = {}, closeButton }) => {
  const { id = null } = params
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { data, loaded } = useSelector(({ tickets }) => tickets)

  const isNew = useMemo(() => !id, [id])
  const breakpoint = useBreakpoint()
  const messageContainerRef = useRef(null)
  const [smallHeader, setSmallHeader] = useState(false)
  let lastScrollTop = 0
  let lock = false

  const isFirstNewTicket = useMemo(() => {
    if (!loaded) {
      return undefined
    }
    return data.length === 0
  }, [loaded, data])

  const handleScroll = () => {
    global.requestAnimationFrame(() => {
      if (
        !messageContainerRef.current ||
        lock === true ||
        messageContainerRef.current.clientHeight > messageContainerRef.current.scrollHeight + 150
      ) {
        return
      }

      lock = true
      setSmallHeader(lastScrollTop > messageContainerRef.current.scrollTop)
      lastScrollTop = messageContainerRef.current.scrollTop

      setTimeout(() => {
        lock = false
      }, 1000)
    })
  }

  useLayoutEffect(() => {
    messageContainerRef.current &&
      messageContainerRef.current.addEventListener('scroll', debounce(handleScroll, 250), {
        passive: true,
      })

    return () => {
      messageContainerRef.current &&
        messageContainerRef.current.removeEventListener('scroll', debounce(handleScroll, 250))
    }
  }, [messageContainerRef.current])

  const {
    title,
    status,
    created_at,
    closed_at,
    updated_at,
    messages = [],
    loading,
  } = useSelector(({ ticket }) => ticket)

  useEffect(() => {
    if (id) {
      dispatch({ type: FETCH_TICKET, payload: { id } })
    } else {
      dispatch({
        type: SET_LOADING,
        payload: { loading: false },
      })
    }

    return () => {
      dispatch({ type: CLEAR })
    }
  }, [id])

  useEffect(() => {
    dispatch(
      initialize(
        'NewTicketMessageForm',
        isNew
          ? {
              title: params.title ?? '',
              text: params.text ?? '',
              attachments: [],
            }
          : {
              text: '',
              attachments: [],
            },
        false,
      ),
    )
  }, [isNew, params])

  if (!loaded || loading) return <Placeholder />

  return (
    <>
      <Grid
        direction={'column'}
        className={styles['ticket-container']}
        gap={32}
      >
        <Grid
          className={clsx(styles['header-container'], {
            [styles['header-container_small']]: smallHeader,
          })}
        >
          <Grid
            direction={'row'}
            wrap={false}
            justifyContent={'flex-start'}
            flexGrow={1}
          >
            <Grid
              direction={'column'}
              gap={16}
              flexGrow={1}
              flexShrink={1}
            >
              <Grid
                flexDirection={'row'}
                wrap={false}
                justifyContent={'space-between'}
              >
                {isFirstNewTicket === false && (
                  <Breadcrumbs
                    className={'padding-0 margin-bottom-0'}
                    links={[
                      {
                        title: t('common::buttons.back'),
                        onClick: () =>
                          dispatch({ type: PANEL_NAVIGATE, payload: { route: 'list' } }),
                      },
                    ]}
                  />
                )}
                <Grid flexJustify={'flex-end'}>{closeButton}</Grid>
              </Grid>

              <PageHeader
                className={clsx(styles['header'], {
                  [styles['header_small']]: smallHeader,
                })}
              >
                {isFirstNewTicket
                  ? t('tickets::first_new_ticket')
                  : isNew
                  ? t('tickets::new_ticket')
                  : title}
              </PageHeader>
            </Grid>
          </Grid>

          <Grid gap={12}>
            {!isNew && (
              <>
                <Grid
                  justifyContent={'stretch'}
                  alignItems={'center'}
                  wrap={!breakpoint.md ? 'wrap' : 'nowrap'}
                  gap={!breakpoint.md ? 12 : 16}
                >
                  <Grid
                    wrap={!breakpoint.md}
                    alignItems={'center'}
                    gap={8}
                  >
                    <StatusBadge
                      size={'xs'}
                      variant={'Body-10'}
                      status={status}
                      className={styles['header__status-badge']}
                    />
                    <Typography
                      component={'div'}
                      variant={'Body-10'}
                      noMargin
                      color={'Label-Fifth'}
                      className={styles['header__ticket-id']}
                    >
                      #{id}
                    </Typography>

                    {!smallHeader && (
                      <Typography
                        component={'div'}
                        variant={'Body-10'}
                        noMargin
                        color={'Label-Secondary'}
                      >
                        {t('common::updated_at')}:{' '}
                        {created_at &&
                          format(
                            parse(updated_at, "yyyy-MM-dd'T'HH:mm:ssXXX", new Date()),
                            'HH:mm, d.MM.yyyy',
                          )}
                      </Typography>
                    )}
                  </Grid>
                  <ToggleButton
                    status={status}
                    id={id}
                    smallHeader={smallHeader}
                    isNew={isNew}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>

        <div
          className={clsx(styles['messages-container'], {
            [styles['messages-container_not-stretched']]: status === 'closed',
          })}
          ref={messageContainerRef}
        >
          {messages.length > 0 &&
            messages.map((message, i) => (
              <Message
                key={'message-' + i}
                {...message}
              />
            ))}
          {status === 'closed' && closed_at && (
            <Grid justifyContent={'center'}>
              <Typography
                color={'Label-Fifth'}
                variant={'Body-15'}
                className={styles['closed-at-notification']}
              >
                {t('tickets::ticket_closed_at', {
                  date: format(
                    parse(closed_at, "yyyy-MM-dd'T'HH:mm:ssXXX", new Date()),
                    'HH:mm d.MM.yyyy',
                  ),
                })}
              </Typography>
            </Grid>
          )}
        </div>

        {status !== 'closed' && (
          <Grid className={clsx(styles['new-message-container'])}>
            <NewTicketMessage
              newTicket={isNew}
              id={id}
              asyncValidate={validator(validationSchema(isNew))}
              onSuccess={(ticketState) =>
                isNew
                  ? dispatch({
                      type: PANEL_NAVIGATE,
                      payload: {
                        route: 'ticket',
                        params: { id: ticketState.id },
                      },
                    })
                  : dispatch({
                      type: SET_DATA,
                      payload: ticketState,
                    })
              }
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default Ticket

Ticket.propTypes = {
  params: PropTypes.object,
  closeButton: PropTypes.element,
}
